import { AlertTriangle } from "react-feather"
import { roleApi } from "@/api/client/role"
import type { RoleList200RolesItem } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "@/hooks/use-toast"

interface DeleteRoleModalProps {
  isOpen: boolean
  onClose: () => void
  role: RoleList200RolesItem | null
  onRefetch: () => void
}

export function DeleteRoleModal({ isOpen, onClose, role, onRefetch }: DeleteRoleModalProps) {
  const deleteRoleMutation = roleApi.useRoleDelete({
    mutation: {
      onError: (error) =>
        toast({
          title: "ロールの削除に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })

  const handleDelete = () => {
    if (!role?.organizationId) return

    deleteRoleMutation.mutate(
      { organizationId: role.organizationId, role: role.role },
      {
        onSuccess: () => {
          toast({
            title: "ロールを削除しました",
          })
          onRefetch()
          onClose()
        },
      }
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            本当に削除してよろしいですか？
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {role?.organizationName}の「{role?.label}」を削除します。この操作は取り消せません。
          このロールが割り当てられている管理者がいる場合は削除できません。
        </DialogDescription>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={deleteRoleMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteRoleMutation.isPending || !role?.organizationId}
          >
            {deleteRoleMutation.isPending ? "削除中..." : "削除する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
